/**
 * PerksSection — Manage Clan's Perks tab (Task 4).
 *
 * Encapsulates: quota strip, add-perk form, existing-perks list, and the
 * pending-redemptions queue. Uses these endpoints:
 *   GET  /api/clans/mine/perks
 *   GET  /api/clans/mine/perks/quota
 *   POST /api/clans/mine/perks
 *   PATCH /api/clans/mine/perks/:id
 *   DELETE /api/clans/mine/perks/:id
 *   GET  /api/clans/mine/perks/redemptions
 *   POST /api/clans/mine/perks/redemptions/:id/fulfill
 *   POST /api/clans/mine/perks/redemptions/:id/cancel
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiErrorDetail } from '../api';
import { formatPts } from '../lib/format';

const DELIVERY_FORMATS = [
  { key: 'digital',  label: '💻 Digital' },
  { key: 'live',     label: '🎙️ Live' },
  { key: 'physical', label: '📦 Physical' },
  { key: 'custom',   label: '✨ Custom' },
];

const EMPTY_FORM = {
  title: '',
  description: '',
  delivery_format: 'digital',
  template_key: null,
  required_bits: 0,
  points_cost: 10,
  supply_limit: '',   // '' = unlimited
};

// Templates come from the quota endpoint (source-of-truth: backend PERK_TEMPLATES).
// We render them as picker chips so creators can start from a proven pattern.

const fmtPts = (n) => `${Math.round(Number(n) * 100) / 100} pts`;

export default function PerksSection({ clan, onToast }) {
  const navigate = useNavigate();
  const [perks, setPerks] = useState([]);
  const [quota, setQuota] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);   // null = create, id = edit
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [expandedRedemption, setExpandedRedemption] = useState(null);
  const [fulfillNote, setFulfillNote] = useState('');

  // Current Bit price (approx) — used to hint "≈ $37.50 in market value" beside required_bits
  const currentBitPrice = useMemo(() => {
    if (!clan) return 0;
    const n = Number(clan.bits_minted || 1);
    const mult = Number(clan.multiplier || 0);
    return mult * (n + 1);   // price of the *next* bit
  }, [clan]);

  const load = useCallback(async () => {
    try {
      const [pRes, qRes, rRes] = await Promise.all([
        api.get('/clans/mine/perks'),
        api.get('/clans/mine/perks/quota'),
        api.get('/clans/mine/perks/redemptions'),
      ]);
      setPerks(pRes.data.perks || []);
      setQuota(qRes.data);
      setRedemptions(rRes.data.redemptions || []);
      setPendingCount(rRes.data.pending_count || 0);
    } catch (ex) {
      onToast?.({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not load perks.', kind: 'error' });
    }
  }, [onToast]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const applyTemplate = (tpl) => {
    setForm((f) => ({
      ...f,
      template_key: tpl.key,
      title: tpl.label,
      delivery_format: tpl.format || f.delivery_format,
    }));
  };

  const startEdit = (perk) => {
    setEditingId(perk.id);
    setForm({
      title: perk.title,
      description: perk.description,
      delivery_format: perk.delivery_format,
      template_key: perk.template_key,
      required_bits: perk.required_bits || 0,
      points_cost: perk.points_cost || 0,
      supply_limit: perk.supply_limit == null ? '' : String(perk.supply_limit),
    });
    setShowForm(true);
  };

  const submit = async () => {
    if (busy) return;
    if (!form.title || form.title.length < 3) {
      onToast?.({ msg: 'Title must be at least 3 characters.', kind: 'error' });
      return;
    }
    setBusy(true);
    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      delivery_format: form.delivery_format,
      template_key: form.template_key || null,
      required_bits: Math.max(0, parseInt(form.required_bits || 0, 10)),
      points_cost: Math.max(0, Number(form.points_cost || 0)),
      supply_limit: form.supply_limit === '' ? null : Math.max(1, parseInt(form.supply_limit, 10)),
    };
    try {
      if (editingId) {
        await api.patch(`/clans/mine/perks/${editingId}`, payload);
        onToast?.({ msg: '✓ Perk updated' });
      } else {
        const { data } = await api.post('/clans/mine/perks', payload);
        const fee = data.fee_paid_pts || 0;
        onToast?.({ msg: fee > 0 ? `✓ Perk published (${fee} pts fee deducted)` : '✓ Perk published — free founding slot!' });
      }
      resetForm();
      await load();
    } catch (ex) {
      onToast?.({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not save perk.', kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const del = async (perk) => {
    if (!window.confirm(`Delete "${perk.title}"? This can't be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/clans/mine/perks/${perk.id}`);
      onToast?.({ msg: '✓ Perk deleted' });
      await load();
    } catch (ex) {
      onToast?.({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not delete.', kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (perk) => {
    setBusy(true);
    try {
      await api.patch(`/clans/mine/perks/${perk.id}`, { is_active: !perk.is_active });
      await load();
    } catch (ex) {
      onToast?.({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not update.', kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const fulfill = async (redemption) => {
    setBusy(true);
    try {
      await api.post(`/clans/mine/perks/redemptions/${redemption.id}/fulfill`, {
        creator_note: fulfillNote,
      });
      onToast?.({ msg: '✓ Marked delivered — fan notified' });
      setFulfillNote('');
      setExpandedRedemption(null);
      await load();
    } catch (ex) {
      onToast?.({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not fulfill.', kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (redemption) => {
    if (!window.confirm(`Cancel this redemption? ${redemption.points_paid} pts will be refunded to ${redemption.fan_display_name || 'the fan'}.`)) return;
    setBusy(true);
    try {
      await api.post(`/clans/mine/perks/redemptions/${redemption.id}/cancel`, {
        reason: 'cancelled by creator',
      });
      onToast?.({ msg: `✓ Cancelled — ${redemption.points_paid} pts refunded` });
      await load();
    } catch (ex) {
      onToast?.({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not cancel.', kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  if (!quota) {
    return (
      <div className="mc-section-card" data-testid="mc-perks-section">
        <div className="mc-section-head"><span className="mc-section-title">🎁 Redeemable perks</span></div>
        <p className="mc-section-sub">Loading…</p>
      </div>
    );
  }

  const activeCount = perks.filter((p) => p.is_active).length;
  const canAddMore = activeCount < quota.cap;
  const feeChip = quota.next_perk_fee_pts > 0
    ? `Next perk: ${quota.next_perk_fee_pts} pts fee`
    : quota.remaining_founding_slots > 0
    ? `✨ FREE (${quota.remaining_founding_slots} founding slots left)`
    : '✨ FREE forever (founding creator)';
  const feeGood = quota.next_perk_fee_pts === 0;

  return (
    <div className="mc-section-card" data-testid="mc-perks-section">
      <div className="mc-section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className="mc-section-title">🎁 Redeemable perks</span>
        <span style={{ fontSize: 12, opacity: 0.7 }}>{activeCount}/{quota.cap} active</span>
      </div>
      <p className="mc-section-sub">
        Sell 1:1 calls, drops, and shoutouts to your Bit holders. Holders spend Points to redeem — <strong>Bits stay in their wallet</strong> as the eligibility key. You keep 100% of the Points.
      </p>

      {/* Quota + fee status strip */}
      <div
        style={{
          padding: '10px 14px', borderRadius: 10, marginBottom: 12,
          background: feeGood ? 'rgba(74,222,128,0.08)' : 'rgba(255,183,77,0.08)',
          border: `1px solid ${feeGood ? 'rgba(74,222,128,0.25)' : 'rgba(255,183,77,0.25)'}`,
          display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, alignItems: 'center',
        }}
        data-testid="mc-perks-quota"
      >
        <span style={{ fontSize: 13, fontWeight: 600 }}>{feeChip}</span>
        <span style={{ fontSize: 12, opacity: 0.75 }}>
          Balance: <strong>{formatPts(quota.points_balance, 2)} pts</strong>
        </span>
      </div>

      {/* Pending redemptions queue — most important to surface */}
      {pendingCount > 0 && (
        <div style={{ marginBottom: 14 }} data-testid="mc-perks-pending">
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: '#00B4FF' }}>
            📬 Pending redemptions ({pendingCount}) — deliver these to earn 5-star reviews
          </div>
          {redemptions.filter((r) => r.status === 'pending').map((r) => (
            <div
              key={r.id}
              style={{
                padding: 12, borderRadius: 10, marginBottom: 8,
                background: 'rgba(0,180,255,0.05)',
                border: '1px solid rgba(0,180,255,0.2)',
              }}
              data-testid={`mc-perks-redemption-${r.id}`}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 220px' }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>
                    {r.fan_display_name || r.fan_handle} · {r.perk_title}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                    +{fmtPts(r.points_paid)} earned · {new Date(r.created_at).toLocaleString()}
                  </div>
                  {r.fan_note && (
                    <div style={{ marginTop: 6, fontSize: 13, fontStyle: 'italic', opacity: 0.9 }}>
                      "{r.fan_note}"
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button
                    className="mc-btn-primary"
                    onClick={() => setExpandedRedemption(expandedRedemption === r.id ? null : r.id)}
                    disabled={busy}
                    style={{ padding: '6px 12px', fontSize: 12 }}
                    data-testid={`mc-perks-fulfill-btn-${r.id}`}
                  >
                    {expandedRedemption === r.id ? 'Close' : '✓ Deliver'}
                  </button>
                  {r.fan_handle && (
                    <button
                      className="mc-btn-secondary"
                      onClick={() => navigate(`/messages/${r.fan_handle}`)}
                      disabled={busy}
                      style={{ padding: '6px 12px', fontSize: 12 }}
                      data-testid={`mc-perks-message-btn-${r.id}`}
                      title={`Message ${r.fan_display_name || r.fan_handle} — ask for clarification, share a link, etc.`}
                    >
                      💬 Message
                    </button>
                  )}
                  <button
                    className="mc-btn-secondary"
                    onClick={() => cancel(r)}
                    disabled={busy}
                    style={{ padding: '6px 12px', fontSize: 12 }}
                    data-testid={`mc-perks-cancel-btn-${r.id}`}
                  >
                    Cancel
                  </button>
                </div>
              </div>
              {expandedRedemption === r.id && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed rgba(255,255,255,0.1)' }}>
                  <textarea
                    className="mc-textarea"
                    placeholder="Optional note to the fan (e.g., Zoom link, tracking number)"
                    value={fulfillNote}
                    onChange={(e) => setFulfillNote(e.target.value)}
                    rows={2}
                    maxLength={300}
                    data-testid={`mc-perks-fulfill-note-${r.id}`}
                  />
                  <button
                    className="mc-btn-primary"
                    onClick={() => fulfill(r)}
                    disabled={busy}
                    style={{ marginTop: 8 }}
                    data-testid={`mc-perks-fulfill-confirm-${r.id}`}
                  >
                    Confirm delivered
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add new perk button */}
      {!showForm && canAddMore && (
        <button
          className="mc-btn-primary"
          onClick={() => { setForm(EMPTY_FORM); setEditingId(null); setShowForm(true); }}
          disabled={busy}
          data-testid="mc-perks-add-btn"
          style={{ width: '100%', marginBottom: 12 }}
        >
          + Add a Perk
        </button>
      )}
      {!showForm && !canAddMore && (
        <div style={{ padding: 10, textAlign: 'center', fontSize: 12, opacity: 0.7, background: 'rgba(255,255,255,0.03)', borderRadius: 8, marginBottom: 12 }}>
          {quota.cap}-perk cap reached. Delete or deactivate one to add more.
        </div>
      )}

      {/* Create / edit form */}
      {showForm && (
        <div
          style={{
            padding: 14, borderRadius: 12, marginBottom: 14,
            background: 'rgba(0,180,255,0.04)',
            border: '1px solid rgba(0,180,255,0.2)',
          }}
          data-testid="mc-perks-form"
        >
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>
            {editingId ? 'Edit Perk' : 'New Perk'}
          </div>

          {/* Template picker (only when creating from scratch) */}
          {!editingId && quota.templates?.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, opacity: 0.6, marginBottom: 6 }}>
                Start from a template
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {quota.templates.map((tpl) => (
                  <button
                    key={tpl.key}
                    type="button"
                    onClick={() => applyTemplate(tpl)}
                    style={{
                      padding: '6px 10px', fontSize: 12, borderRadius: 16,
                      background: form.template_key === tpl.key ? 'rgba(0,180,255,0.25)' : 'rgba(255,255,255,0.05)',
                      border: form.template_key === tpl.key ? '1px solid #00B4FF' : '1px solid rgba(255,255,255,0.1)',
                      color: 'inherit', cursor: 'pointer',
                    }}
                    data-testid={`mc-perks-template-${tpl.key}`}
                  >
                    {tpl.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Title */}
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 12, opacity: 0.75, display: 'block', marginBottom: 4 }}>Title</label>
            <input
              className="mc-input"
              type="text"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="e.g. 15-min 1:1 Video Call"
              maxLength={80}
              data-testid="mc-perks-title"
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 12, opacity: 0.75, display: 'block', marginBottom: 4 }}>Description</label>
            <textarea
              className="mc-textarea"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="What does the fan get when they redeem?"
              rows={3}
              maxLength={500}
              data-testid="mc-perks-description"
            />
          </div>

          {/* Delivery format */}
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 12, opacity: 0.75, display: 'block', marginBottom: 4 }}>Delivery format</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {DELIVERY_FORMATS.map((d) => (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, delivery_format: d.key }))}
                  style={{
                    padding: '6px 12px', fontSize: 12, borderRadius: 8,
                    background: form.delivery_format === d.key ? 'rgba(181,132,245,0.25)' : 'rgba(255,255,255,0.05)',
                    border: form.delivery_format === d.key ? '1px solid #B584F5' : '1px solid rgba(255,255,255,0.1)',
                    color: 'inherit', cursor: 'pointer',
                  }}
                  data-testid={`mc-perks-format-${d.key}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          {/* Points cost — the only always-visible pricing input.
              Required Bits (tier gating) is tucked into the Advanced block
              below since most perks are "any member can redeem". */}
          <div style={{ marginBottom: 10 }}>
            <div>
              <label style={{ fontSize: 12, opacity: 0.75, display: 'block', marginBottom: 4 }}>
                Points cost <span style={{ opacity: 0.6, fontWeight: 400 }}>· fan pays you</span>
              </label>
              <input
                className="mc-input"
                type="number"
                min={0}
                step={0.5}
                value={form.points_cost}
                onChange={(e) => setForm((f) => ({ ...f, points_cost: e.target.value }))}
                data-testid="mc-perks-points-cost"
              />
              <div style={{ fontSize: 11, opacity: 0.55, marginTop: 4 }}>
                Debited from the fan's Points balance, credited 100% to yours. Set 0 for a free perk to eligible holders.
              </div>
            </div>
          </div>

          {/* Advanced · tier gating (collapsed by default). Most creators
              never touch this — Clan-level min_bits already gates entry, so
              required_bits: 0 means "any Clan member can redeem". Only open
              this if you want a perk exclusive to bigger supporters. */}
          <details
            style={{
              marginTop: 4, padding: '10px 12px',
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(150,200,255,0.10)',
              borderRadius: 10,
            }}
            data-testid="mc-perks-advanced-details"
          >
            <summary
              style={{ cursor: 'pointer', fontSize: 12, fontWeight: 700, opacity: 0.85, userSelect: 'none' }}
              data-testid="mc-perks-advanced-summary"
            >
              ⚙ Advanced · tier gating
              {form.required_bits > 0 && (
                <span style={{ marginLeft: 8, fontSize: 10, padding: '1px 8px', borderRadius: 999, background: 'rgba(0,180,255,0.18)', color: '#00B4FF', fontWeight: 800 }}>
                  ≥ {form.required_bits} Bits required
                </span>
              )}
            </summary>
            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: 12, opacity: 0.75, display: 'block', marginBottom: 4 }}>
                Minimum Bits to redeem <span style={{ opacity: 0.6, fontWeight: 400 }}>· make this perk exclusive to bigger holders</span>
              </label>
              <input
                className="mc-input"
                type="number"
                min={0}
                value={form.required_bits}
                onChange={(e) => setForm((f) => ({ ...f, required_bits: e.target.value }))}
                data-testid="mc-perks-required-bits"
              />
              {form.required_bits > 0 && currentBitPrice > 0 && (
                <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
                  ≈ ${(form.required_bits * currentBitPrice).toFixed(2)} at current Bit price
                </div>
              )}
              <div style={{ fontSize: 11, opacity: 0.55, marginTop: 4, lineHeight: 1.45 }}>
                Every Clan member can redeem by default. Raise this to lock a premium perk to your bigger holders — e.g. <b>10 Bits</b> for a monthly 1:1, <b>25 Bits</b> for signed merch. Bits stay in the fan's wallet — this is a gate, not a cost.
              </div>
            </div>
          </details>

          {/* Supply limit */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, opacity: 0.75, display: 'block', marginBottom: 4 }}>
              Supply limit (optional)
            </label>
            <input
              className="mc-input"
              type="number"
              min={1}
              value={form.supply_limit}
              onChange={(e) => setForm((f) => ({ ...f, supply_limit: e.target.value }))}
              placeholder="Blank = unlimited"
              data-testid="mc-perks-supply-limit"
            />
            <div style={{ fontSize: 11, opacity: 0.55, marginTop: 4 }}>
              Cap the total redemptions if it&apos;s a limited drop (e.g., 10 signed prints).
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="mc-btn-secondary" onClick={resetForm} disabled={busy} data-testid="mc-perks-cancel">
              Cancel
            </button>
            <button className="mc-btn-primary" onClick={submit} disabled={busy || !form.title} data-testid="mc-perks-publish">
              {busy ? 'Saving…' : editingId ? 'Save changes' : `Publish ${quota.next_perk_fee_pts > 0 ? `(${quota.next_perk_fee_pts} pts)` : '(FREE)'}`}
            </button>
          </div>
        </div>
      )}

      {/* Existing perks list */}
      {perks.length === 0 && !showForm && (
        <div style={{ padding: 16, textAlign: 'center', fontSize: 13, opacity: 0.6 }}>
          No perks yet. Tap <strong>+ Add a Perk</strong> to start selling access.
        </div>
      )}
      {perks.length > 0 && (
        <div>
          {perks.map((p) => (
            <div
              key={p.id}
              style={{
                padding: 12, borderRadius: 10, marginBottom: 8,
                background: p.is_active ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.01)',
                border: p.is_active ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(255,255,255,0.04)',
                opacity: p.is_active ? 1 : 0.55,
              }}
              data-testid={`mc-perks-item-${p.id}`}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 220px' }}>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>
                    {p.title}
                    {!p.is_active && <span style={{ marginLeft: 8, fontSize: 11, padding: '2px 6px', borderRadius: 4, background: 'rgba(255,255,255,0.08)' }}>inactive</span>}
                  </div>
                  {p.description && (
                    <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 4 }}>{p.description}</div>
                  )}
                  <div style={{ fontSize: 12, opacity: 0.75, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <span>{fmtPts(p.points_cost)}</span>
                    {p.required_bits > 0 && <span>· Requires {p.required_bits} Bits</span>}
                    <span>· {p.redemptions_count} redeemed{p.supply_limit ? ` / ${p.supply_limit}` : ''}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    className="mc-btn-secondary"
                    onClick={() => toggleActive(p)}
                    disabled={busy}
                    style={{ padding: '4px 10px', fontSize: 12 }}
                    data-testid={`mc-perks-toggle-${p.id}`}
                  >
                    {p.is_active ? 'Pause' : 'Activate'}
                  </button>
                  <button
                    className="mc-btn-secondary"
                    onClick={() => startEdit(p)}
                    disabled={busy}
                    style={{ padding: '4px 10px', fontSize: 12 }}
                    data-testid={`mc-perks-edit-${p.id}`}
                  >
                    Edit
                  </button>
                  <button
                    className="mc-btn-secondary"
                    onClick={() => del(p)}
                    disabled={busy}
                    style={{ padding: '4px 10px', fontSize: 12 }}
                    data-testid={`mc-perks-delete-${p.id}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
